import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { message } from 'antd';
import ReactTable from 'react-table';
import _get from 'lodash/get';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import GaragePageHeader from '../components/GaragePageHeader';
import Menu from '../components/Menu';

import { getOperationForGarage, patchGarageOperation, createGarageOperation } from './actions';

import 'react-table/react-table.css';
import GarageAddOperationForm from './add-operation';
import { getGarageById } from '../GarageEditPage/actions';

const positivePriceValidation = {
  regexp: /^(0|[1-9][0-9]*)(\.[0-9]{1,2})?$/,
  message: 'price should be positive (ex : 9.99)',
};

/**
 * ********************************** NOTE **********************************
 *
 * property `type` is defined and used only in the Frontend
 * as in garage-operation table the `price` column was updated to allow decimals
 * this will let us to use percentages and can continue to use integers for prices as before
 *
 * See `PriceFormToApi, PriceApiToForm, PercentageToApi, ApiToPercentage` in ./actions.js
 *
 * At every request the operation object has to go through these functions in order to save the correct value for each type
 *
 * type price: 10(frontend) -> 1000(backend) -> 10(frontend)
 * type percentage: 10(frontend) -> 0.1(backend) -> 10(frontend)
 */
const OPERATIONS = [
  { name: 'MOT', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  {
    name: 'MOT with full/major service',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  { name: 'Oil', defaultValue: '5', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  {
    name: 'Premium Oil',
    defaultValue: '1.00',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  { name: 'Labour rate', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  { name: 'Discounted Labour Rate', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  {
    name: 'Contrôle Technique',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '€',
  },
  {
    name: 'Garage C&D',
    defaultValue: '12',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Fixter C&D fee',
    defaultValue: '10',
    priceValidation: positivePriceValidation,
    type: 'percentage',
    symbol: '%',
  },
  {
    name: 'Fixter C&D fee upper threshold',
    defaultValue: '20',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Booking Fee Percentage',
    defaultValue: '1.25',
    priceValidation: positivePriceValidation,
    type: 'percentage',
    symbol: '%',
  },
  { name: 'In & Out Car Wash', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  { name: 'Exterior Car Wash', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  { name: 'Front Wheel Alignment Fee', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },

  // Garage bonuses
  { name: 'MOT Bonus', priceValidation: positivePriceValidation, type: 'price', symbol: '£' },
  {
    name: 'Repair & Service Bonus',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Collection & Delivery Bonus',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },

  // Cross-sell Products
  {
    name: 'Fuel Treatment',
    defaultValue: '13.8',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Engine Flush',
    defaultValue: '14.2',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Brake Fluid Flush',
    defaultValue: '42.43',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Coolant Flush',
    defaultValue: '53.02',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '£',
  },
  {
    name: 'Purge du liquide de frein',
    defaultValue: '61.23',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '€',
  },
  {
    name: 'Purge du liquide de refroidissement',
    defaultValue: '46.03',
    priceValidation: positivePriceValidation,
    type: 'price',
    symbol: '€',
  },
];

const DivStyled = styled.div`
  padding: 8px 16px;
  margin: 0;
  background: #fbfbfb;

  .ReactTable {
    background: #fff;
  }
`;

export class GarageOperationsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      operations: null,
      garage: null,
    };
    this.closePageHandler = this.closePageHandler.bind(this);
    this.renderEditable = this.renderEditable.bind(this);
    this.handleFormOp = this.handleFormOp.bind(this);
  }

  get garageId() {
    return this.props.match.params.garageId;
  }

  renderEditable(cellInfo) {
    return (
      <div
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => this.updateCellPrice(cellInfo, e.target.innerHTML)}
        onKeyPress={(e) => {
          e.persist();
          if (e.key == 'Enter') {
            e.preventDefault();
            e.target.blur();
          }
        }}
        dangerouslySetInnerHTML={{
          __html: this.state.operations[cellInfo.index][cellInfo.column.id],
        }}
      />
    );
  }

  updateCellPrice(cellInfo, newValue) {
    const op = OPERATIONS.find((op) => op.name == cellInfo.original.operationId);

    if (!op) {
      // shoudnt happend
      return message.error('operation price validation not found on the FE');
    }

    if (!op.priceValidation.regexp.test(newValue)) {
      return message.error(op.priceValidation.message);
    }

    const operations = [...this.state.operations];
    operations[cellInfo.index][cellInfo.column.id] = newValue;
    patchGarageOperation(this.garageId, operations[cellInfo.index])
      .then(() => message.info('garage operation updated', 1))
      .then(() => this.setState({ operations }))
      .catch((err) =>
        message.error(_get(err, 'response.data.message') || 'could not update garage operation', 3)
      );
  }

  componentDidMount() {
    const { garageId } = this;
    if (!this.state.garage && garageId) {
      getGarageById(garageId).then((garage) => this.setState((st) => ({ ...st, garage })));
    }
    getOperationForGarage(garageId).then((operations) => this.setState((st) => ({ ...st, operations })));
  }

  closePageHandler() {
    this.props.history.push('/');
  }

  handleFormOp(operation) {
    return createGarageOperation(this.garageId, operation)
      .then((operation) => {
        const operations = [...this.state.operations];
        operations.push(operation);
        return this.setState({ operations });
      })
      .then(() => message.info('garage operation created', 1))
      .catch(() => message.error('could not create garage operation', 1));
  }

  addCurrencyToTableData(fetchedOperations){
    /**
     * Currency is purely visual and not stored in the DB so we need to add the symbols to the operations fetched from the DB.
     * This allows us to show the correct currency prefix.
     */
    return fetchedOperations.map( (fetchedOp) => {
      const opConfig = OPERATIONS.find( configObj => configObj.name === fetchedOp.operationId);
      return {...fetchedOp, symbol: opConfig?.symbol}
    });
  };

  render() {
    const { operations, garage } = this.state;

    if (!operations) return <Loading />;

    const existingOperation = operations || [];
    const existingOperationIds = existingOperation.map((op) => op.operationId);

    const { garageId } = this;

    const columns = [
      {
        Header: 'Operation name',
        accessor: 'operationId',
      },
      {
        Header: 'Price (pre VAT) / Percentage %',
        accessor: 'symbol',
        style: { textAlign: 'right' },
      },
      {
        Header: 'Value',
        accessor: 'price',
        Cell: this.renderEditable,
      },
    ];

    return (
      <Container>
        <GaragePageHeader history={history} name={garage?.businessName} />
        <Menu selectedItem="operations" garageId={garageId} history={this.props.history} />
        <DivStyled>
          {operations && (
            <ReactTable
              data={this.addCurrencyToTableData(operations)}
              columns={columns}
              showPagination={false}
              defaultPageSize={Math.max(operations.length, 10)}
            />
          )}
          <GarageAddOperationForm
            operations={OPERATIONS.filter((op) => existingOperationIds.indexOf(op.name) < 0)}
            onUpdate={this.handleFormOp}
          />
        </DivStyled>
      </Container>
    );
  }
}

export default withRouter(GarageOperationsPage);
